import { IRoomSummary } from "@backend/src/shared/schemas/schemas";
import { copyDevicesFromASpaceToAnotherSpace, IDevice } from "@multiconsult-group/multiplacer-core";
import { IAppThunk } from "../../../../app/store";
import { selectActivePartialMcbimSpace } from "../../../../McBim/mcbimSelectors";
import { selectCachedMcbimBuildingStoriesForCurrentProject } from "../../../designSelector";
import {
  addNonPublischedDevicesFromAlgosInRedux,
  getHeightFromFirstCeilingGrid,
  removeNonPublischedDevicesFromAlgosInRedux,
  selectAdvancedCopySelectedTargetRoomNumbers,
  setAdvancedCopySelectedTargetRoomNumbersInRedux,
  thunkToast,
} from "../../../designSlice";
import { selectHttpDevicesInRoom } from "../../deviceSlice";
import { withWorldZInCoordinate } from "../../deviceUtils";
import {
  selectCachedMatchingDevicesForAdvancedCopy,
  selectCachedMatchingRoomsForAdvancedCopy,
  selectCachedMatchingSpacesForAdvancedCopy,
} from "./advancedCopyDevicesSelectors";

export const thunkCopyDevicesToMultipleSelectedTargetRooms = (): IAppThunk => {
  return (dispatch, getState) => {
    const state = getState();
    const sourceSpace = selectActivePartialMcbimSpace(state);
    const sourceDevices = selectHttpDevicesInRoom(state);
    const selectedTargetRoomNumbers = selectAdvancedCopySelectedTargetRoomNumbers(state);
    const targetSpaces = selectCachedMatchingSpacesForAdvancedCopy(state);
    const targetRooms = selectCachedMatchingRoomsForAdvancedCopy(state);
    const existingTargetDevices = selectCachedMatchingDevicesForAdvancedCopy(state);
    const nonPublishedDevices = state.design.nonPublischedDevicesFromAlgos;
    const activeFloorElevationM = state.design.floorAndJob?.elevation;
    const buildingStories = selectCachedMcbimBuildingStoriesForCurrentProject(state);

    if (!sourceSpace) {
      dispatch(
        thunkToast({
          text: "Kilderom mangler.",
          header: "Feil",
          isSuccess: false,
        })
      );
      return;
    }

    if (selectedTargetRoomNumbers.length === 0) {
      dispatch(
        thunkToast({
          text: "Velg minst ett målrom i tabellen.",
          header: "Ingen rom valgt",
          isSuccess: false,
        })
      );
      return;
    }

    const copiedDevicesToAdd: IDevice[] = [];
    const copiedRoomNumbers: string[] = [];
    const skippedExistingDevices: string[] = [];
    const skippedMissingData: string[] = [];
    const skippedLowSimilarity: string[] = [];

    for (const roomNumber of selectedTargetRoomNumbers) {
      const targetSpace = targetSpaces.find((space) => space.name === roomNumber);
      const targetRoom = targetRooms[roomNumber];

      if (!targetSpace) {
        skippedMissingData.push(roomNumber);
        continue;
      }

      if (sourceSpace.name === targetSpace.name) {
        continue;
      }

      const targetHasExistingDevices = existingTargetDevices.some(
        (device) => device.roomNumber === roomNumber
      );

      if (targetHasExistingDevices) {
        skippedExistingDevices.push(roomNumber);
        continue;
      }

      const existingNonPublishedInTarget = nonPublishedDevices.filter(
        (device) => device.roomNumber === roomNumber
      );

      if (existingNonPublishedInTarget.length > 0) {
        dispatch(
          removeNonPublischedDevicesFromAlgosInRedux(
            existingNonPublishedInTarget.map((device) => String(device._id))
          )
        );
      }

      const copiedDevices = copyDevicesFromASpaceToAnotherSpace({
        devicesToCopy: sourceDevices as IDevice[],
        spaceToCopyFrom: sourceSpace,
        spaceToCopyTo: targetSpace,
        roomToCopyTo: (targetRoom ?? undefined) as IRoomSummary | undefined,
      });

      if (copiedDevices.length === 0) {
        skippedLowSimilarity.push(roomNumber);
        continue;
      }

      const buildingStory = buildingStories.find(
        (story) => story.name === targetSpace.buildingStory?.name
      );
      const heightFromFloor = getHeightFromFirstCeilingGrid(targetSpace.ceilingGrids ?? []) ?? 2700;
      const floorElevationM = buildingStory?.elevation ?? activeFloorElevationM;

      const enrichedCopiedDevices = copiedDevices.map((device) => ({
        ...device,
        floor: targetSpace.buildingStory?.name ?? device.floor,
        roomName: targetSpace.longName ?? targetRoom?.roomName ?? device.roomName,
        bottomEdgeHeightAboveFloorMm: heightFromFloor,
        coordinate: withWorldZInCoordinate(device.coordinate, floorElevationM, heightFromFloor),
      }));

      copiedDevicesToAdd.push(...enrichedCopiedDevices);
      copiedRoomNumbers.push(roomNumber);
    }

    if (copiedDevicesToAdd.length > 0) {
      dispatch(addNonPublischedDevicesFromAlgosInRedux(copiedDevicesToAdd));
    }

    dispatch(setAdvancedCopySelectedTargetRoomNumbersInRedux([]));

    if (copiedRoomNumbers.length === 0) {
      dispatch(
        thunkToast({
          text: "Ingen av de valgte målrommene kunne kopieres.",
          header: "Ingen kopiering utført",
          isSuccess: false,
        })
      );
      return;
    }

    const summaryParts = [`Kopierte til ${copiedRoomNumbers.length} rom`];

    if (skippedExistingDevices.length > 0) {
      summaryParts.push(
        `${skippedExistingDevices.length} hoppet over fordi de allerede har komponenter`
      );
    }

    if (skippedLowSimilarity.length > 0) {
      summaryParts.push(`${skippedLowSimilarity.length} hoppet over på grunn av lav romlikhet`);
    }

    if (skippedMissingData.length > 0) {
      summaryParts.push(`${skippedMissingData.length} hoppet over fordi romdata manglet`);
    }

    dispatch(
      thunkToast({
        text: `${summaryParts.join(". ")}.`,
        header: "Kopiering fullført",
        isSuccess: true,
      })
    );
  };
};
